import { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Tabs, Tab } from 'react-bootstrap';
import SortingIcon from 'components/SortingIcon';

import ToggleSortByTag from './components/ToggleSortByTag';
import { sortOptions } from './components/ToggleSortByTag/utils';
import SignalRConnectionStatusIcon from './components/SignalRConnectionStatusIcon';
import ChangeRequestsList from './components/ChangeRequestsList';

import { SearchFilter, Statistikk, EndringsMelding } from 'services/QAApi/types';

import Style from './index.module.css';
import MottakerTypeahead from 'components/MottakerTypeahead';
import GrossistgruppePicker from 'components/GrossistgruppePicker';
import CommonLoader from 'components/CommonLoader';
import PopoverQACardList from './PopoverQACardList';
import NewChangeRequestModal from './components/NewChangeRequestModal';
import ChangeRequestUploadModal from './components/ChangeRequestUploadModal';

interface Props {
  filter: SearchFilter;
  onFilterChanged: (value: string, type: string) => void;
  onFilterTagsChanged: (values: string[]) => void;
  onFilterGlnChanged: (value: string[]) => void;
  onFilterTabChanged: (value: string) => void;
  signalRConnectionStatus: string;
  loadingChangeRequest: boolean;
  statistikk: Statistikk;
  changeRequests: EndringsMelding[];
  folgesOppRequests: EndringsMelding[];
  processedChangeRequests: EndringsMelding[];
  rejectedChangeRequests: EndringsMelding[];
  allChangeRequests: EndringsMelding[];
  selectedChangeRequest: EndringsMelding;
  onChangeRequestSelected: (
    changeRequest: EndringsMelding,
    nextChangeRequest?: EndringsMelding,
  ) => void;
}

const QACardList = (props: Props) => {
  const [antallAktive, setAntallAktive] = useState<number>(props.statistikk.antallAktive | 0);
  const [antallFolgesOpp, setAntallFolgesOpp] = useState<number>(
    props.statistikk.antallFolgesOpp | 0,
  );
  const [kilde, setKilde] = useState<string | null>(props.filter.source);
  const [showCustomKildeInput, setShowCustomKildeInput] = useState<boolean>(false);

  const handleCardClicked = (
    changeRequest: EndringsMelding,
    nextChangeRequest?: EndringsMelding,
  ) => {
    // next is for being able to go to next changeRequest after submitting
    if (changeRequest && nextChangeRequest) {
      props.onChangeRequestSelected(changeRequest, nextChangeRequest);
    } else if (changeRequest && !nextChangeRequest) {
      props.onChangeRequestSelected(changeRequest);
    }
  };

  const predefinedKilder = [
    'GlnApi',
    'Tine',
    'BladCentralen',
    'Langaard',
    'Salgsdata',
    'Snitch',
    'FilImport',
    'PublicApi',
  ];

  useEffect(() => {
    if (!kilde) {
      setAntallAktive(props.statistikk.antallAktive);
      setAntallFolgesOpp(props.statistikk.antallFolgesOpp);
    } else {
      const item = props.statistikk.perKilde.find((x) => x.kilde === kilde);
      setAntallAktive(item?.antallAktive ?? 0);
      setAntallFolgesOpp(item?.antallFolgesOpp ?? 0);
    }
  }, [kilde, props.statistikk]);

  const loadingContent = (
    <div className={Style.loadingListPaceholder}>
      <h4 className={Style.loadingListHeader}>Laster endringsmeldinger..</h4>
      <CommonLoader />
    </div>
  );

  const handleGrossistgruppeSelectionChanged = useCallback((grossistGln: string[]) => {
    props.onFilterGlnChanged(grossistGln);
  }, []);

  return (
    <div className={Style.cardListContainer}>
      <Row className="mb-3">
        <Col>
          <ChangeRequestUploadModal />
        </Col>
        <Col>
          <NewChangeRequestModal />
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="filterFormGroup">
            <Form.Control
              type="filter"
              value={props.filter.query}
              placeholder="Filtrer endringsmeldinger..."
              onChange={(e) => props.onFilterChanged(e.target.value, 'query')}
            />
          </Form.Group>
        </Col>
      </Row>
      <Form className="mb-3">
        <Row className="mt-3">
          <Col>
            <Form.Group controlId="sourceFilter">
              <Form.Label visuallyHidden>Filtrer på kilde</Form.Label>
              <Form.Select
                value={showCustomKildeInput ? 'custom' : (kilde ?? '')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    setShowCustomKildeInput(true);
                    setKilde('');
                    props.onFilterChanged('', 'source');
                  } else {
                    setShowCustomKildeInput(false);
                    setKilde(value);
                    props.onFilterChanged(value, 'source');
                  }
                }}
              >
                <option value="">--Filtrer på kilde--</option>
                <option value="GlnApi">GlnApi</option>
                <option value="Tine">Tine</option>
                <option value="BladCentralen">BladCentralen</option>
                <option value="Langaard">Langaard</option>
                <option value="Salgsdata">Salgsdata</option>
                <option value="Snitch">Snitch</option>
                <option value="FilImport">FilImport</option>
                <option value="PublicApi">PublicApi</option>
                <option value="custom">Annen kilde</option>
              </Form.Select>
              {showCustomKildeInput && (
                <Form.Control
                  type="text"
                  className="mt-2 mb-3"
                  value={kilde ?? ''}
                  placeholder="Skriv egen kilde..."
                  onChange={(e) => {
                    setKilde(e.target.value);
                    props.onFilterChanged(e.target.value, 'source');
                  }}
                />
              )}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="takeFilter" className="mb-3">
              <Form.Select
                value={props.filter.take}
                onChange={(e) => props.onFilterChanged(e.target.value, 'take')}
              >
                <option>25</option>
                <option>50</option>
                <option>100</option>
                <option>200</option>
                <option>500</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <MottakerTypeahead
              onHandleChange={(mottaker) =>
                props.onFilterChanged(
                  mottaker ? mottaker.tsKundeId : null,
                  'leverandorKundenummerHosTsKundeId',
                )
              }
              placeholderText="Filtrer på mottaker"
            />
          </Col>
          <Col>
            <GrossistgruppePicker
              onSelectionChanged={handleGrossistgruppeSelectionChanged}
              findSelectedGrossists={() => props.filter.grossistKundenummerHosGln}
            />
          </Col>
        </Row>
      </Form>

      {/* Filter buttons and status toolbar - combined row */}
      <Row className="align-items-end">
        <Col xs={11}>
          <ToggleSortByTag
            options={sortOptions}
            onChange={props.onFilterTagsChanged}
            tags={props.filter.tags}
          />
        </Col>
        <Col xs={1} style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}>
          <div className={Style.statusToolbar}>
            <div onClick={() => props.onFilterChanged('', 'orderBy')} style={{ cursor: 'pointer' }}>
              <SortingIcon
                sortDirection={
                  props.filter.orderBy === 'ascending'
                    ? 'asc'
                    : props.filter.orderBy === 'descending'
                      ? 'desc'
                      : ''
                }
              />
            </div>
            <SignalRConnectionStatusIcon status={props.signalRConnectionStatus} />
            <PopoverQACardList searchFilter={props.filter} />
          </div>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey={props.filter.query ? 'alle' : 'ubehandlet'}
        onSelect={(e) => props.onFilterTabChanged(e)}
      >
        <Tab eventKey="ubehandlet" title={antallAktive > 0 ? `Aktive (${antallAktive})` : 'Aktive'}>
          <div className={Style.cardListContent}>
            {props.loadingChangeRequest ? (
              loadingContent
            ) : (
              <ChangeRequestsList
                changeRequests={props.changeRequests}
                selectedChangeRequest={props.selectedChangeRequest}
                onCardClicked={handleCardClicked}
              />
            )}
          </div>
        </Tab>
        <Tab
          eventKey="folgesOpp"
          title={antallFolgesOpp > 0 ? `Følges opp (${antallFolgesOpp})` : 'Følges opp'}
        >
          <div className={Style.cardListContent}>
            {props.loadingChangeRequest ? (
              loadingContent
            ) : (
              <ChangeRequestsList
                changeRequests={props.folgesOppRequests}
                selectedChangeRequest={props.selectedChangeRequest}
                onCardClicked={handleCardClicked}
              />
            )}
          </div>
        </Tab>
        <Tab eventKey="avvist" title="Avvist">
          <div className={Style.cardListContent}>
            {props.loadingChangeRequest ? (
              loadingContent
            ) : (
              <ChangeRequestsList
                changeRequests={props.rejectedChangeRequests}
                selectedChangeRequest={props.selectedChangeRequest}
                onCardClicked={handleCardClicked}
              />
            )}
          </div>
        </Tab>
        <Tab eventKey="behandlet" title="Behandlet">
          <div className={Style.cardListContent}>
            {props.loadingChangeRequest ? (
              loadingContent
            ) : (
              <ChangeRequestsList
                changeRequests={props.processedChangeRequests}
                selectedChangeRequest={props.selectedChangeRequest}
                onCardClicked={handleCardClicked}
              />
            )}
          </div>
        </Tab>

        <Tab eventKey="alle" title="Alle">
          <div className={Style.cardListContent}>
            {props.loadingChangeRequest ? (
              loadingContent
            ) : (
              <ChangeRequestsList
                changeRequests={props.allChangeRequests}
                selectedChangeRequest={props.selectedChangeRequest}
                onCardClicked={handleCardClicked}
              />
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default QACardList;
